import React, { useState, useEffect } from 'react';
import api from '../../http';

const ManualEntryWizard = ({ eventId, initialCompanyNumber, onComplete, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [companyNumber, setCompanyNumber] = useState('');
  const [defaultProgram, setDefaultProgram] = useState('');

  const [candidates, setCandidates] = useState([
    { first_name: '', last_name: '', program_name: '' },
    { first_name: '', last_name: '', program_name: '' }
  ]);

  useEffect(() => {
    if (initialCompanyNumber) {
      setCompanyNumber(initialCompanyNumber.toString());
    }
  }, [initialCompanyNumber]);

  const handleDefaultProgramChange = (val) => {
    const oldDefault = defaultProgram;
    setDefaultProgram(val);
    // Auto-update candidates who still have the old default or are empty
    setCandidates(
      candidates.map((c) => ({
        ...c,
        program_name: c.program_name === oldDefault || c.program_name === '' ? val : c.program_name
      }))
    );
  };

  const handleCandidateChange = (idx, field, value) => {
    const newCandidates = [...candidates];
    newCandidates[idx][field] = value;
    setCandidates(newCandidates);
  };

  const addCandidateRow = () => {
    setCandidates([...candidates, { first_name: '', last_name: '', program_name: defaultProgram }]);
  };

  const removeCandidateRow = (idx) => {
    setCandidates(candidates.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!eventId) {
      setError('No event selected.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare payload
      const payload = {
        name: companyNumber,
        program_name: defaultProgram,
        candidates: candidates
          .filter((c) => c.first_name && c.last_name)
          .map((c) => ({
            first_name: c.first_name,
            last_name: c.last_name,
            program_name: c.program_name || defaultProgram // Fallback to default if empty
          }))
      };

      await api.post(`/api/test-events/${eventId}/companies`, payload);

      // Reset form
      setCandidates([
        { first_name: '', last_name: '', program_name: defaultProgram },
        { first_name: '', last_name: '', program_name: defaultProgram }
      ]);
      // Keep default program? Yes, likely useful for batch entry.
      // companyNumber will update via parent prop change usually, but we can clear it or let parent handle it.
      // If parent updates initialCompanyNumber, useEffect will catch it.

      if (onComplete) onComplete();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to create company.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section" style={{ height: '100%', margin: 0 }}>
      <h2
        style={{
          marginTop: 0,
          marginBottom: '1rem',
          borderBottom: '1px solid #eee',
          paddingBottom: '0.5rem'
        }}
      >
        Add Company
      </h2>

      {error && (
        <div className="alert danger" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}
      >
        <div>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Company Number
          </label>
          <input
            className="input"
            value={companyNumber}
            onChange={(e) => setCompanyNumber(e.target.value)}
            placeholder="e.g. 101"
          />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Default Program (Optional)
          </label>
          <input
            className="input"
            value={defaultProgram}
            onChange={(e) => handleDefaultProgramChange(e.target.value)}
            placeholder="e.g. Fire Academy Class 2025"
          />
          <small style={{ color: '#666' }}>Auto-fills new candidate rows</small>
        </div>
      </div>

      <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Candidates</h3>
      <div style={{ marginBottom: '1rem', maxHeight: '50vh', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr
              style={{ borderBottom: '2px solid #eee', textAlign: 'left', background: '#f9f9f9' }}
            >
              <th style={{ padding: '0.5rem' }}>First Name</th>
              <th style={{ padding: '0.5rem' }}>Last Name</th>
              <th style={{ padding: '0.5rem' }}>Program</th>
              <th style={{ padding: '0.5rem', width: '40px' }}></th>
            </tr>
          </thead>
          <tbody>
            {candidates.map((cand, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid #f5f5f5' }}>
                <td style={{ padding: '0.5rem' }}>
                  <input
                    className="input"
                    placeholder="First Name"
                    value={cand.first_name}
                    onChange={(e) => handleCandidateChange(idx, 'first_name', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input
                    className="input"
                    placeholder="Last Name"
                    value={cand.last_name}
                    onChange={(e) => handleCandidateChange(idx, 'last_name', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input
                    className="input"
                    placeholder="Program Name"
                    value={cand.program_name}
                    onChange={(e) => handleCandidateChange(idx, 'program_name', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td style={{ textAlign: 'center' }}>
                  {candidates.length > 1 && (
                    <button
                      style={{
                        color: '#e53e3e',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        fontWeight: 'bold'
                      }}
                      onClick={() => removeCandidateRow(idx)}
                      title="Remove Candidate"
                      tabIndex="-1"
                    >
                      &times;
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        className="btn btn-secondary btn-sm"
        onClick={addCandidateRow}
        style={{ marginBottom: '1.5rem', width: '100%', borderStyle: 'dashed' }}
      >
        + Add Another Candidate
      </button>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '1rem',
          borderTop: '1px solid #eee',
          paddingTop: '1rem'
        }}
      >
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={
            loading || !companyNumber || candidates.every((c) => !c.first_name && !c.last_name)
          }
        >
          {loading ? 'Saving...' : 'Save Company'}
        </button>
      </div>
    </section>
  );
};

export default ManualEntryWizard;
